import { useCallback, useEffect, useRef } from 'react';

import { useAudioTrigger } from '../../controllers/audioController/hooks';
import { GameCommunicationBetSender } from '../../controllers/gameCommunication/senders/GameCommunicationBetSender';
import { Logger } from '../../logger/Logger';
import { useCoreDispatch } from '../../store/CoreStore';
import { useBetSlice } from '../../store/slices/bets/selectors';
import { betActions } from '../../store/slices/bets/slice';
import type { BetPayloadType, DragBufferType } from '../../store/slices/bets/types';
import { useGameStateSlice } from '../../store/slices/game/selectors';

import type { DragAndDropTargetConnectedArgs, DragAndDropTargetConnectedReturnType } from './types';

const betSender = new GameCommunicationBetSender();

export const useDragAndDropTargetConnected: (
  args: DragAndDropTargetConnectedArgs
) => DragAndDropTargetConnectedReturnType = ({ betSpot, ref, clickSound, onBet }) => {
  const targetLocalRef = useRef<HTMLElement>(null);
  const targetRef = ref || targetLocalRef;
  const dispatch = useCoreDispatch();
  const dragBuffer = useBetSlice.getPreliminaryDragBuffer();
  const balance = useGameStateSlice.getDeductibleBalance();
  const isBetsOpenStatus = useGameStateSlice.isBetsOpenStatus();
  const decimals = useGameStateSlice.getCurrencyInfo()?.digitsNumber || 2;
  const audioTrigger = useAudioTrigger();

  const promotedDragBuffer = dragBuffer && !dragBuffer.preliminary ? dragBuffer : null;

  const handlePointerUp = useCallback(
    (event: PointerEvent): void => {
      if (!dragBuffer) return;

      if (!isBetsOpenStatus) {
        dispatch(betActions.setDragBuffer(null));
        return;
      }

      if (dragBuffer.preliminary === false || dragBuffer.from !== betSpot) {
        event.stopImmediatePropagation();

        if (betSpot && !dragBuffer.preliminary && dragBuffer.from !== betSpot) {
          const newBet = createNewBet({ ...dragBuffer, to: betSpot }, balance, decimals);
          onBet ? onBet(newBet) : dispatch(betActions.pushBet(newBet));
          if (clickSound) {
            audioTrigger.play({ name: clickSound });
          }
          betSender.dispatch();
        }
      }

      dispatch(betActions.setDragBuffer(null));
      Logger.logEvent('click', 'dragAndDrop', Date.now(), 1, '', false);
    },
    [audioTrigger, balance, betSpot, clickSound, decimals, dispatch, dragBuffer, isBetsOpenStatus, onBet]
  );

  useEffect(() => {
    const targetElement = targetRef.current;
    targetElement?.addEventListener('pointerup', handlePointerUp, { passive: true });

    return (): void => {
      targetElement?.removeEventListener('pointerup', handlePointerUp);
    };
  }, [handlePointerUp, targetRef]);

  return [
    targetRef,
    { buffer: dragBuffer, isDragging: !!promotedDragBuffer, isDraggedFrom: promotedDragBuffer?.from === betSpot },
  ];
};

// Helper functions
function createNewBet(
  dragData: DragBufferType & { to: number },
  balance: number,
  decimals: number = 2
): BetPayloadType {
  return {
    bet: {
      betSpots: {
        [dragData.from!]: {
          value: -dragData.value!,
        },
        [dragData.to!]: {
          value: dragData.value!,
        },
      },
    },
    balance,
    decimals,
  };
}
